import { useEffect } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { AuthLoadingScreen } from "../components/auth/AuthLoadingScreen";
import { useResilientSession } from "../hooks/useResilientSession";
import { isNative } from "../lib/platform";
import {
  clearReturnDestination,
  readReturnDestination,
  rememberReturnDestination,
} from "../lib/returnDestination";

export const AuthProtectedRoute = () => {
  const { data: session, isPending } = useResilientSession();
  const location = useLocation();
  const currentPath = `${location.pathname}${location.search}`;
  const isSignedIn = Boolean(session);

  // Return-to-destination. Every sign-in exit lands on /dashboard, which is
  // inside this guard, so this is the one place a signed-out visitor's
  // destination (an emailed /n/<id> link, say) can be carried across sign-in.
  // Signed out: remember where they were going. Signed in: the value has just
  // been used (or is stale), so drop it — the redirect below renders once.
  useEffect(() => {
    if (isPending) return;
    if (isSignedIn) {
      clearReturnDestination();
      return;
    }
    rememberReturnDestination(currentPath);
  }, [isPending, isSignedIn, currentPath]);

  // Show loading while session is loading
  if (isPending) {
    return <AuthLoadingScreen />;
  }

  // If no session, redirect to sign-in
  if (!session) {
    return (
      <Navigate
        state={{
          from: location.pathname,
        }}
        replace
        to="/sign-in"
      />
    );
  }

  const returnDestination = readReturnDestination(isNative());
  if (returnDestination !== null && returnDestination !== currentPath) {
    return <Navigate replace to={returnDestination} />;
  }

  return <Outlet />;
};
